<script>
import DeveloperProfileResource from '../resources/developerprofile'
import SkillResource from '../resources/skill'
import RoleResource from '../resources/role'
import ProfileCell from '../components/ProfileCell.vue'
import ProfileSummary from '../components/ProfileSummary.vue'
import NoResults from '@/components/NoResults.vue'
import ResourceSearch from '../components/ResourceSearch.vue'
import RegionSelect from '../components/RegionSelect.vue'
import MetroAreaResource from '../resources/metroarea'
import LookingForResource from '../resources/lookingfor'

export default {
    name: 'RecruitSource',
    components: {
        ProfileCell,
        ProfileSummary,
        ResourceSearch,
        RegionSelect,
        NoResults,
    },
    props: {
        companyResource: {
            type: Object,
            required: true,
        },
    },
    computed: {
        filterLength() {
            return Object.values(this.selectedFilterResources).reduce((value, curValue) => {
                let a = Array.isArray(value) ? value.length : value
                let b = Array.isArray(curValue) ? curValue.length : 1
                return a + b
            })
        },
        nextPreviousSelectedProfileResources() {
            if(this.selectedProfileResource) {
                for(let i=0; i<this.profileResources.length; i++) {
                    if(this.profileResources[i].id === this.selectedProfileResource.id) {
                        return {
                            next: this.profileResources[i + 1],
                            previous: this.profileResources[i - 1]
                        }
                    }
                }
            }

            return {
                next: null, previous: null
            }
        }
    },
    data() {
        return {
            loading: true,
            pageData: null,
            pageCurrent: 1,
            listingResources: [],
            profileResources: [],
            profileInterestedResources: [],
            profilePassedResources: [],
            profileModalVisible: false,
            lookingForResources: [],
            roleResources: [],
            selectedListingResource: null,
            selectedProfileResource: null,
            selectedFilterResources: {
                skills: [],
                regions: [],
                roles: [],
                type: null,
            },
            filtersExpanded: false,
            filtersYearsExperienceRequired: 0,
            filtersPersist: {
                best_first: 'true',
                ordering: '-years_experience',
            },
        }
    },
    watch: {
        async selectedListingResource() {
            this.loading = true
            await this.applyFiltersFromSelectedListing()
            await this.search()
            this.loading = false
        },
        profileResources() {
            window.scroll(0, 0)
        },
    },
    methods: {
        async search() {
            let query = Object.assign({}, this.makeFilters(), this.filtersPersist)
            let result = await DeveloperProfileResource.list({ query })
            this.profileResources = result.resources
            this.pageData = result
            this.pageCurrent = 1
        },
        async applyFiltersFromSelectedListing() {
            let relatedManagersToSelect = ['skills', 'regions', 'type']
            await this.selectedListingResource.resolveRelated({ managers: relatedManagersToSelect })

            relatedManagersToSelect.forEach((managerKey) => {
                let manager = this.selectedListingResource.managers[managerKey]
                if (manager.many) {
                    this.selectedFilterResources[managerKey] = this.selectedListingResource.managers[managerKey].resources
                } else {
                    this.selectedFilterResources[managerKey] = this.selectedListingResource.managers[managerKey].resource
                }
            })
        },
        makeFilters() {
            let extractIds = (resourceList) => resourceList.map((resource) => resource.id).join(',')
            return {
                curate: this.selectedListingResource.id,
                skills__in: extractIds(this.selectedFilterResources.skills),
                location_preferred__in: extractIds(this.selectedFilterResources.regions),
                looking_for__in: this.selectedFilterResources.type.id,
                primary_role__in: extractIds(this.selectedFilterResources.roles),
                years_experience__gte: this.filtersYearsExperienceRequired,
            }
        },
        async onClickSearch() {
            this.loading = true
            this.filtersExpanded = false
            await this.search()
            this.loading = false
        },
        async onPageChange(num, force = false) {
            if (force || Number(num) !== Number(this.pageCurrent)) {
                this.loading = true
                this.pageData = await this.pageData.goToPage(num)
                this.profileResources = this.pageData.resources
                this.pageCurrent = num
                this.loading = false
            }
        },
        async onInterested(profileResource) {
            if (await this.selectedListingResource.interested(profileResource.id) && !this.profileInterestedResources.includes(profileResource)) {
                this.profileInterestedResources.push(profileResource)
                
                this.$bvToast.toast(`${profileResource} has been added to list of sourced candidates for "${this.selectedListingResource}"`, {
                    title: 'Browse Candidates',
                    to: { name: 'recruitJobView', params: { companyId: this.companyResource.id, listingId: this.selectedListingResource.id } },
                    variant: 'primary',
                    autoHideDelay: 5000,
                })

                return true
            }
            return false
        },
        async onPass(profileResource) {
            if (await this.selectedListingResource.pass(profileResource.id) && !this.profilePassedResources.includes(profileResource)) {
                this.profilePassedResources.push(profileResource)
                return true
            }
            return false
        },
        isInterested(profileResource) {
            return this.profileInterestedResources.includes(profileResource)
        },
        isPassed(profileResource) {
            return this.profilePassedResources.includes(profileResource)
        },
        selectProfile(profileResource) {
            this.selectedProfileResource = profileResource
            this.profileModalVisible = true
        },
        async sendMessageToProfile(profileResource) {
            let userResourceManager = profileResource.rel('user')
            await userResourceManager.resolve()
            this.$store.commit('composeMessage', userResourceManager.resource)
            this.profileModalVisible = false
        }
    },
    async mounted() {
        let [listingResources, lookingForResources, roleResources] = await Promise.all([this.companyResource.listingsAll({ is_archived: false }), LookingForResource.all(), RoleResource.all()])

        this.listingResources = listingResources
        this.lookingForResources = lookingForResources
        this.roleResources = roleResources

        if (this.listingResources.length > 0) {
            let selectedListingId = this.$route.query.listing || this.listingResources[0].id
            this.selectedListingResource = this.listingResources.find((listingResource) => {
                return listingResource.id === selectedListingId
            })
        }

        this.loading = false
    },
}
</script>

<template>
    <div v-page-title="'Browse Candidates'">
        <b-card>
            <b-row>
                <b-col md="4">
                    <b-form-group label="Job Listing">
                        <select v-if="listingResources.length > 0" class="form-control custom-select mb-2 mr-sm-2 mb-sm-0" v-model="selectedListingResource">
                            <option v-for="(listingResource, i) in listingResources" :key="i" :value="listingResource">{{ listingResource }}</option>
                        </select>
                        <select v-else class="form-control custom-select mb-2 mr-sm-2 mb-sm-0">
                            <option disabled>Loading...</option>
                        </select>
                    </b-form-group>
                </b-col>

                <b-col md="4">
                    <b-form-group label="Looking For">
                        <select v-if="lookingForResources.length > 0" class="form-control custom-select mb-2 mr-sm-2 mb-sm-0" v-model="selectedFilterResources.type">
                            <option v-for="(resource, i) in lookingForResources" :key="i" :value="resource">{{ resource }}</option>
                        </select>
                    </b-form-group>
                </b-col>

                <b-col>
                    <b-form-group>
                        <template slot="label">
                            <br />
                        </template>
                        <b-button-group class="float-right">
                            <b-button variant="outline-secondary" @click="filtersExpanded = !filtersExpanded"
                                ><span v-if="filterLength > 0"
                                    >Filters <b-badge variant="dark">{{ filterLength }}</b-badge></span
                                ></b-button
                            >
                            <b-button variant="primary" @click="onClickSearch">Search</b-button>
                        </b-button-group>
                    </b-form-group>
                </b-col>
            </b-row>

            <hr v-if="filtersExpanded" />

            <b-row class="mt-3" v-if="filtersExpanded">
                <b-col>
                    <b-form-group label="Developer Skills">
                        <resource-search
                            :resourceClass="$resources.SkillResource"
                            :filter="(resource) => !~selectedFilterResources.skills.map((resource) => resource.id).indexOf(resource.id)"
                            @select="(resource) => selectedFilterResources.skills.push(resource)"
                            name="skills"
                            height="250"
                            placeholder="Start typing to search... (eg. TypeScript)"
                        ></resource-search>
                        <b-badge variant="primary" v-for="(skillResource, i) in selectedFilterResources.skills" :key="i" class="mr-1">
                            {{ skillResource }}
                            <b-button type="button" class="close skill-badge ml-1" @click="selectedFilterResources.skills.splice(selectedFilterResources.skills.indexOf(skillResource), 1)">×</b-button>
                        </b-badge>
                    </b-form-group>

                    <b-form-group label="Experience Required">
                        <b-form-spinbutton v-model="filtersYearsExperienceRequired" min="0" max="15" step="1" class="col-md-3" inline></b-form-spinbutton> Years
                    </b-form-group>

                    <b-form-group label="Role">
                        <b-form-checkbox v-model="selectedFilterResources.roles" v-for="(roleResource, i) in roleResources" :key="i" :value="roleResource">
                            {{ roleResource }}
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>

                <b-col>
                    <b-form-group label="Regions">
                        <region-select v-model="selectedFilterResources.regions" />
                        <b-form-group v-if="selectedFilterResources.regions.length > 0">
                            <template slot="label">
                                Candidates located in:
                                <b-badge href="javascript:{}" variant="light" @click="selectedFilterResources.regions = []" class="float-right">Reset</b-badge>
                            </template>
                            <b-badge v-for="(region, i) in selectedFilterResources.regions" :key="i" class="mb-1 mr-1">{{ region }}</b-badge>
                        </b-form-group>
                    </b-form-group>
                </b-col>
            </b-row>
        </b-card>

        <div class="mt-3">
            <div v-if="!loading">
                <div v-if="!listingResources.length">
                    <no-results heading-text="Please Create a Job Listing">
                        <p>To browse candidates, you must have an active job listing.</p>
                        <p><b-button variant="primary" :to="{ name: 'recruitJobAdd', params: { companyId: companyResource.id } }">List a New Job</b-button></p>
                    </no-results>
                </div>
                <div v-else>
                    <div v-if="profileResources.length > 0">
                        <b-card v-for="(profileResource, i) in profileResources" :key="i" class="mb-3">
                            <profile-cell :profile="profileResource" :listing="selectedListingResource" v-if="!isPassed(profileResource)" @click="selectProfile(profileResource)">
                                <b-button-group size="sm">
                                    <b-button variant="outline-secondary" @click="sendMessageToProfile(profileResource)"><b-icon icon="envelope" /></b-button>
                                    <b-button :variant="isInterested(profileResource) ? 'secondary' : 'outline-secondary'" @click="onInterested(profileResource)">Interested</b-button>
                                    <b-button :variant="isPassed(profileResource) ? 'secondary' : 'outline-secondary'" @click="onPass(profileResource)">Pass</b-button>
                                </b-button-group>
                            </profile-cell>
                            <div v-else>
                                <strong>{{ profileResource }}</strong> has been moved to <router-link :to="{ name: 'recruitJobView', params: { listingId: selectedListingResource.id } }">passed candidates</router-link> for "{{ selectedListingResource }}".
                            </div>
                        </b-card>

                        <b-pagination
                            @input="onPageChange"
                            :value="pageCurrent"
                            :total-rows="pageData.count"
                            :per-page="pageData.pageSize"
                            prev-text="Prev"
                            next-text="Next"
                            align="center"
                            hide-ellipsis
                        >
                        </b-pagination>
                    </div>

                    <no-results v-else>
                        <p>Try <a href="javascript:{}" @click="filtersExpanded = true">expanding your search criteria</a> for this position</p>
                    </no-results>
                </div>
            </div>

            <div v-else>
                <b-card v-for="i in new Array(10)" :key="i" class="mb-3">
                    <profile-cell />
                </b-card>
            </div>
        </div>

        <b-modal v-model="profileModalVisible" size="xl" :title-html="`Reviewing <strong>${selectedProfileResource}</strong> for <em>${selectedListingResource}</em>`" no-fade centered scrollable>
            <template #modal-footer>
                <b-button-group>
                    <b-button :variant="profileInterestedResources.includes(selectedProfileResource) ? 'secondary' : 'outline-secondary'" @click="onInterested(selectedProfileResource)">Interested</b-button>
                    <b-button :variant="profilePassedResources.includes(selectedProfileResource) ? 'secondary' : 'outline-secondary'" @click="onPass(selectedProfileResource)">Pass</b-button>
                </b-button-group>
                <b-button variant="outline-secondary" @click="sendMessageToProfile(selectedProfileResource)"><b-icon icon="envelope" /> Send Message</b-button>
                <b-button-group class="float-right">
                    <b-button variant="outline-secondary" :disabled="!nextPreviousSelectedProfileResources.previous" @click="selectedProfileResource = nextPreviousSelectedProfileResources.previous">Previous</b-button>
                    <b-button variant="outline-secondary" :disabled="!nextPreviousSelectedProfileResources.next" @click="selectedProfileResource = nextPreviousSelectedProfileResources.next">Next</b-button>
                </b-button-group>
            </template>
            <profile-summary :profile="selectedProfileResource" :listing="selectedListingResource">
            </profile-summary>
        </b-modal>
    </div>
</template>

<style lang="scss">
.close.skill-badge {
    font-size: 1em;
}
</style>
